import { useCallback, useRef } from 'react';
import { applyListBreak } from '../utils/autoList';
import { track } from '../analytics';

// Перевод строки внутри textarea. insertParagraph textarea не порождает, но
// Firefox исторически шлёт именно его — держим оба.
const LINE_BREAK = new Set(['insertLineBreak', 'insertParagraph']);

// Узлы, на которые слушатель уже повешен. Нужен, потому что React 18 зовёт
// ref-колбэк на каждый ре-рендер, а один и тот же колбэк может обслуживать
// несколько textarea (список полей через .map — так в Настройках).
// Снимать слушатель не надо: он живёт на самом узле и уезжает вместе с ним
// в GC. React 18 не умеет cleanup из ref-колбэка, а по одному `null` понять,
// КАКОЙ узел размонтировали, нельзя — отсюда WeakSet вместо бухгалтерии.
const attached = new WeakSet();

/**
 * Автосписки в textarea: «1. » / «- » + Enter продолжают список.
 * Возвращает ref-колбэк: <textarea ref={useAutoList('task_description')} />
 *
 * ⚠️ Это именно ref, а не onKeyDown. Слушаем нативный `beforeinput`, потому что
 * keydown при системном голосовом вводе (macOS Dictation, диктовка на телефоне)
 * не даёт отличить диктовку от IME-композиции — см. комментарий в utils/autoList.js.
 * React-синтетический onBeforeInput не подходит: он не отдаёт inputType.
 *
 * PostHog: событие шлётся ОДИН раз на поле за жизнь компонента — иначе каждый
 * перевод строки улетал бы отдельным capture и забивал поток.
 *
 * @param {string} field  идентификатор поля для аналитики (snake_case)
 */
export function useAutoList(field) {
  const tracked = useRef({});

  return useCallback(node => {
    if (!node || attached.has(node)) return;
    attached.add(node);

    const onBeforeInput = e => {
      if (!LINE_BREAK.has(e.inputType)) return;
      // Событие неотменяемое (редкие сборки WebView) — не трогаем, пусть
      // вставится обычный перевод строки, это хуже, но не сломано
      if (!e.cancelable) return;
      applyListBreak(node, {
        prevent: () => e.preventDefault(),
        onContinue: type => {
          const key = `${field}:${type}`;
          if (tracked.current[key]) return;
          tracked.current[key] = true;
          track('autolist_continued', { field, list_type: type });
        },
      });
    };

    node.addEventListener('beforeinput', onBeforeInput);
  }, [field]);
}

export default useAutoList;
